import { DropdownMenu as Menu, Dialog as Sheet } from "radix-ui";
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "../../cn.ts";
import { Kbd } from "../../primitives/display/Kbd/Kbd.tsx";
import { menuPanelClassName } from "../DropdownMenu/menuItems.tsx";
import { usePortalContainer, useStageBoundary } from "../portalContainer.tsx";
import { scrimClassName } from "../surfaces.ts";

/**
 * § Overlays, ShortcutMenu (015 `contracts/screens.md` § 020 Accounts and pipeline, the block of
 * 2026-09-27, The quick-create menu): a small menu whose rows each carry a letter that takes the row
 * while the menu is open, under a heading and over a foot line. At `--bp-md` and up it is shadcn/ui's
 * Dropdown Menu over Radix, anchored to its trigger, with the rows `menuItems.tsx` draws and the
 * letter as the Kbd of § Primitives at the row's end; below `--bp-md` it is a bottom sheet, the
 * Radix Dialog of the Drawer with the scrim of § Overlays, holding the same rows as a `menu` of
 * `menuitem` buttons that the arrows, `Home` and `End` move between, the first focused on opening.
 *
 * What differs from the Dropdown Menu, and why: a row's letter acts on the key alone rather than
 * through Radix's typeahead, which matches a label's first letter and would take "New client" for
 * `n`; and the sheet exists because a menu anchored to a 24 px control at the top of a phone puts
 * its rows under the thumb's worst reach, where a sheet puts them under its best.
 */
export interface ShortcutMenuItem {
  readonly id: string;
  readonly label: string;
  readonly icon: ReactNode;
  /** One letter, drawn upper case, taking the row while the menu is open. */
  readonly letter: string;
  readonly onSelect: () => void;
}

export interface ShortcutMenuProps {
  readonly trigger: ReactNode;
  readonly heading: string;
  readonly items: readonly ShortcutMenuItem[];
  /** The quiet line under the rows, such as how the menu is opened from anywhere. */
  readonly foot?: ReactNode;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Below `--bp-md`, where the menu is a bottom sheet. */
  readonly sheet: boolean;
  /** Radix's hand-back of the focus to the trigger as the menu closes; a row that opens a dialog prevents it, so the focus stays in the dialog. */
  readonly onCloseAutoFocus?: (event: Event) => void;
}

const rowClassName =
  "relative flex min-h-(--menu-item-height) w-full cursor-default items-center gap-2 rounded-sm border-0 bg-transparent px-2 py-1.5 text-start text-sm text-foreground outline-hidden select-none focus:bg-accent focus:text-accent-foreground data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground";
const headingClassName = "px-2 pt-1.5 pb-1 text-caption font-medium text-text-tertiary";
const footClassName = "flex items-center gap-(--space-2) px-2 py-1.5 text-caption text-text-tertiary";

/** The row whose letter a key names, or undefined. */
function rowForKey(items: readonly ShortcutMenuItem[], event: KeyboardEvent): ShortcutMenuItem | undefined {
  if (event.metaKey || event.ctrlKey || event.altKey || event.key.length !== 1) return undefined;
  const key = event.key.toLowerCase();
  return items.find((item) => item.letter.toLowerCase() === key);
}

function RowBody({ item }: { readonly item: ShortcutMenuItem }) {
  return (
    <>
      <span className="inline-flex" aria-hidden="true">
        {item.icon}
      </span>
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {/* The letter is the row's `aria-keyshortcuts`; its cap is drawn for the eye. */}
      <span className="ms-auto inline-flex ps-4" aria-hidden="true">
        <Kbd keys={item.letter.toUpperCase()} />
      </span>
    </>
  );
}

export function ShortcutMenu({ trigger, heading, items, foot, open, onOpenChange, sheet, onCloseAutoFocus }: ShortcutMenuProps) {
  const container = usePortalContainer();
  const boundary = useStageBoundary();
  const take = (item: ShortcutMenuItem) => {
    onOpenChange(false);
    item.onSelect();
  };
  const onLetter = (event: KeyboardEvent) => {
    const item = rowForKey(items, event);
    if (item === undefined) return;
    event.preventDefault();
    event.stopPropagation();
    take(item);
  };

  if (sheet) {
    return (
      <Sheet.Root open={open} onOpenChange={onOpenChange}>
        <Sheet.Trigger asChild>{trigger}</Sheet.Trigger>
        <Sheet.Portal {...(container === undefined ? {} : { container })}>
          <Sheet.Overlay className={scrimClassName} />
          <Sheet.Content
            data-slot="shortcut-menu-sheet"
            aria-describedby={undefined}
            // The rows take the focus themselves, the first one, as the menu's first row does (`SheetRows`).
            onOpenAutoFocus={(event) => {
              event.preventDefault();
            }}
            {...(onCloseAutoFocus === undefined ? {} : { onCloseAutoFocus })}
            className="fixed inset-x-0 bottom-0 z-(--layer-overlay) flex max-h-[calc(100%_-_var(--space-10))] flex-col rounded-t-lg border-t bg-popover p-(--space-2) pb-[max(var(--space-2),env(safe-area-inset-bottom))] text-popover-foreground shadow-lg data-[state=closed]:animate-out data-[state=closed]:duration-(--duration-base) data-[state=closed]:slide-out-to-bottom data-[state=open]:animate-in data-[state=open]:duration-(--duration-slow) data-[state=open]:slide-in-from-bottom"
            onKeyDown={onLetter}
          >
            <Sheet.Title className={headingClassName}>{heading}</Sheet.Title>
            <SheetRows items={items} heading={heading} onTake={take} />
            {foot !== undefined && (
              <>
                <div role="separator" className="-mx-(--space-2) my-1 h-px bg-border" />
                <p className={cn(footClassName, "m-0")}>{foot}</p>
              </>
            )}
          </Sheet.Content>
        </Sheet.Portal>
      </Sheet.Root>
    );
  }

  return (
    <Menu.Root open={open} onOpenChange={onOpenChange}>
      <Menu.Trigger asChild>{trigger}</Menu.Trigger>
      <Menu.Portal {...(container === undefined ? {} : { container })}>
        <Menu.Content
          {...boundary}
          data-slot="shortcut-menu"
          aria-label={heading}
          align="end"
          sideOffset={4}
          collisionPadding={8}
          className={cn(menuPanelClassName, "max-w-[min(var(--menu-width-max),var(--radix-dropdown-menu-content-available-width))] origin-(--radix-dropdown-menu-content-transform-origin) shadow-md")}
          onKeyDown={onLetter}
          {...(onCloseAutoFocus === undefined ? {} : { onCloseAutoFocus })}
        >
          <Menu.Label className={headingClassName}>{heading}</Menu.Label>
          {items.map((item) => (
            <Menu.Item key={item.id} data-slot="shortcut-menu-item" className={rowClassName} aria-keyshortcuts={item.letter.toUpperCase()} onSelect={item.onSelect}>
              <RowBody item={item} />
            </Menu.Item>
          ))}
          {foot !== undefined && (
            <>
              <Menu.Separator className="-mx-1 my-1 h-px bg-border" />
              <Menu.Label className={footClassName}>{foot}</Menu.Label>
            </>
          )}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}

/** The sheet's rows: a `menu` whose arrows, `Home` and `End` move the focus, the first row focused as the sheet opens. */
function SheetRows({ items, heading, onTake }: { readonly items: readonly ShortcutMenuItem[]; readonly heading: string; readonly onTake: (item: ShortcutMenuItem) => void }) {
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    list.current?.querySelector<HTMLElement>("[role='menuitem']")?.focus();
  }, []);
  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const rows = [...(list.current?.querySelectorAll<HTMLElement>("[role='menuitem']") ?? [])];
    const at = rows.findIndex((row) => row === document.activeElement);
    const next = event.key === "ArrowDown" ? (at + 1) % rows.length : event.key === "ArrowUp" ? (at - 1 + rows.length) % rows.length : event.key === "Home" ? 0 : event.key === "End" ? rows.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    rows[next]?.focus();
  };
  return (
    <div ref={list} role="menu" aria-label={heading} className="flex flex-col" onKeyDown={move}>
      {items.map((item) => (
        <button key={item.id} type="button" role="menuitem" tabIndex={-1} aria-keyshortcuts={item.letter.toUpperCase()} data-slot="shortcut-menu-item" className={cn(rowClassName, "min-h-(--target-min) text-base")} onClick={() => { onTake(item); }}>
          <RowBody item={item} />
        </button>
      ))}
    </div>
  );
}
